import { isLoggedIn, logOut } from "./authentication";

import {makeApiUrl} from "../util/config";
import {toCamelCase} from "../util/javascript.js";

export const StatusCodes = {
    SUCCESS: 1,
    FAILED: 2
};

export const ErrorTypes = {
    NETWORK_ERROR: "NETWORK_ERROR",
    REMOTE_ERROR: "REMOTE_ERROR",
    USER_ERROR: "USER_ERROR",
    UNAUTHORIZED: "UNAUTHORIZED",
    ACCESS_DENIED: "ACCESS_DENIED",
    LOCAL_ERROR: "LOCAL_ERROR"
};

export const ContentTypes = {
    JSON: "application/json",
    IMAGE: "image/*",
    PDF:  "application/pdf",
    FORM: "application/x-www-form-urlencoded;charset=UTF-8",
    EXCEL: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    CSV: "text/csv",
};

export class NetworkError extends Error {
    /**
     * @param {string} url
     * @param {string} errorType
     * @param {any} payload
     * @param {string?} message
     * @param {number?} userExceptionType
     */
    constructor(url, errorType, payload, message, userExceptionType) {
        super(message);
        this.url = url;
        this.errorType = errorType;
        this.payload = payload;
        this.message = message;
        this.userExceptionType = userExceptionType;
    }

    /**
     * @return {{NETWORK_ERROR: string, REMOTE_ERROR: string, USER_ERROR: string, UNAUTHORIZED: string, ACCESS_DENIED: string, LOCAL_ERROR: string}}
     * @constructor
     */
    static get ErrorType() {
        return ErrorTypes;
    }
}
// /**
//  * Perform a HTTP POST with the body as json and return the parsed json response.
//  * @param {Object} data - Payload which will be serialized to FORM.
//  * @return {{payload: Object?, message: string?}}
//  */
// function dataToForm(data) {
//     var formBody = [];
//     for (var property in data) {
//         var encodedKey = encodeURIComponent(property);
//         var encodedValue = encodeURIComponent(data[property]);
//         formBody.push(encodedKey + "=" + encodedValue);
//     }
//     formBody = formBody.join("&");
//     return formBody;
// }

/**
 * Replaces illegal characters in URI search string.
 * @param {string} freeTextFilter
 * @returns {string}
 */
export function getEncodedSearchString(freeTextFilter) {
    const freeTextFilterEncoded = freeTextFilter && freeTextFilter.length > 0 ? encodeURIComponent(freeTextFilter) : undefined;
    return freeTextFilterEncoded;
}


/**
 * Perform a HTTP POST with the body as json and return the parsed json response.
 * @param {string} url - The url to post to.
 * @param {Object} data - Payload which will be serialized to JSON.
 * @param {string?} token - A JWT if the user is logged in.
 * @param {string} contentType - We can ask for json or images
 * @param {string} acceptType - We can ask for json or images
 * @return {{payload: Object?, message: string?}}
 * @throws {NetworkError}
 */
export async function postJson(url, data, token, contentType = ContentTypes.JSON, acceptType = ContentTypes.JSON) {
    const headers = new Headers();
    headers.append("Content-Type", contentType);
    headers.append("Accept", acceptType);
    headers.append("Cache-Control", "no-store");
    if (token) {
        headers.append("Authorization", `Bearer ${token}`);
    }

    const mode = undefined; // "cors"

    let httpResponse;
    try {
        httpResponse = await fetch(makeApiUrl(url), {method: "POST", headers, mode, body: JSON.stringify(data), cache: "no-store"});
    } catch (error) {
        throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, error, null);
    }

    if (!httpResponse.ok) {
        let parsedError;
        try {
            parsedError = toCamelCase(await httpResponse.json());
        } catch (error) {
            if(process.env.NODE_ENV !== "production") {
                parsedError = {message:`${httpResponse.status.toString()} ${httpResponse.statusText}`};
            } else {
                parsedError = undefined;
            }
        }

        if (httpResponse.status === 401) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.UNAUTHORIZED, undefined, parsedError && parsedError.displayMessage, parsedError && parsedError.errorCodeAsText);
        } else if (httpResponse.status === 403) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.ACCESS_DENIED, undefined, parsedError && parsedError.displayMessage, parsedError && parsedError.errorCodeAsText);
        } else if (httpResponse.status >= 400 && httpResponse.status < 500) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.USER_ERROR, parsedError && parsedError.payload, parsedError && parsedError.displayMessage, parsedError && parsedError.errorCodeAsText);
        } else if ([502, 504].some(code => code === httpResponse.status)){
            throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, undefined, "Gateway Timeout");
        } else {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.REMOTE_ERROR, parsedError && parsedError.payload, parsedError && parsedError.displayMessage, parsedError && parsedError.errorCodeAsText);
        }
    }

    let parsedResponse;
    try {
        if(contentType === ContentTypes.IMAGE || contentType === ContentTypes.PDF)
            return httpResponse;
        parsedResponse = toCamelCase(await httpResponse.json());
    } catch (error) {
        throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, error, null);
    }
    
    if (parsedResponse.result === StatusCodes.SUCCESS) {
        return {
            payload: parsedResponse.payload,
            message: parsedResponse.message,
        };
    } else {
        throw new NetworkError(makeApiUrl(url), parsedResponse.userExceptionType ? ErrorTypes.USER_ERROR : ErrorTypes.REMOTE_ERROR, parsedResponse.payload, parsedResponse.message, parsedResponse.userExceptionType);
    }
}

/**
 * Skapar en query parameter sträng för objekt med djup.
 * 
 * @param {any} queryParameters 
 * @param {string[]} parentNames
 * @returns {string}
 */
const createQueryParameters = (queryParameters, parentNames) => {
    const isObject = obj => obj && Object.keys(obj).length > 0 && typeof(obj) !== "string";
    if (Array.isArray(queryParameters)) {
        return Object.entries(queryParameters).map(kv => {
            const newParentNames = [...parentNames];
            newParentNames[newParentNames.length - 1] = `${newParentNames[newParentNames.length - 1]}[${kv[0]}]`;
            return createQueryParameters(kv[1], newParentNames);
        }).join("&");
    } else if (isObject(queryParameters)) {
        return Object.entries(queryParameters).filter(kv => kv[1] !== undefined).map(kv => {
            return createQueryParameters(kv[1], parentNames.concat(kv[0]));
        }).join("&");
    } else {
        return `${parentNames.join(".")}=${queryParameters}`;
    }
};

/**
 * Perform a HTTP GET without a body and return the parsed json response.
 * @param {string} url - The url to get.
 * @param {Object.<string, *>?} queryParameters - Key/value map of query parameters to append to the url.
 * @param {string?} token - A JWT if the user is logged in.
 * @param {string} contentType - We can ask for json or images
 * @param {string} acceptType - We can ask for json or images
 * @return {{payload: Object?, message: string, extraInfo: *}}
 * @throws {NetworkError}
 */
export async function getJson(url, queryParameters, token, contentType = ContentTypes.JSON, acceptType = ContentTypes.JSON) {
    const headers = new Headers();
    headers.append("Content-Type", contentType);
    headers.append("Accept", acceptType);
    headers.append("Cache-Control", "no-store");
    if (token) {
        headers.append("Authorization", `Bearer ${token}`);
    }
   
    const mode = undefined; // "cors"

    let httpResponse;
    try {
        const queryParameterString = (queryParameters && Object.values(queryParameters).find(p => p !== undefined) !== undefined) ?`?${createQueryParameters(queryParameters, [])}`:"";
        httpResponse = await fetch(`${makeApiUrl(url)}${queryParameterString}`, {method: "GET", headers, mode, cache: "no-store"});
    } catch (error) {
        throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, error, null);
    }

    if (!httpResponse.ok) {
        let parsedError;
        try {
            parsedError = toCamelCase(await httpResponse.json());
        } catch (error) {
            if(process.env.NODE_ENV !== "production") {
                parsedError = {message:`${httpResponse.status.toString()} ${httpResponse.statusText}`};
            } else {
                parsedError = undefined;
            }
        }

        if (httpResponse.status === 401) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.UNAUTHORIZED, undefined, parsedError && parsedError.displayMessage, parsedError && parsedError.errorCodeAsText);
        } else if (httpResponse.status === 403) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.ACCESS_DENIED, undefined, parsedError && parsedError.displayMessage, parsedError && parsedError.errorCodeAsText);
        } else if (httpResponse.status >= 400 && httpResponse.status < 500) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.USER_ERROR, parsedError && parsedError.payload, parsedError && parsedError.displayMessage, parsedError && parsedError.errorCodeAsText);
        } else if ([502, 504].some(code => code === httpResponse.status)){
            throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, undefined, "Gateway Timeout");
        } else {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.REMOTE_ERROR, parsedError && parsedError.payload, parsedError && parsedError.displayMessage, parsedError && parsedError.errorCodeAsText);
        }
    }

    let parsedResponse;
    try {        
        if(contentType === ContentTypes.IMAGE || contentType === ContentTypes.PDF || contentType === ContentTypes.EXCEL)
            return httpResponse;
        parsedResponse = toCamelCase(await httpResponse.json());
    } catch (error) {
        throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, error, null);
    }

    if (parsedResponse.result === StatusCodes.SUCCESS) {
        return {
            payload: parsedResponse.payload,
            message: parsedResponse.message,
            extraInfo: parsedResponse.extraInfo
        };
    } else {
        throw new NetworkError(makeApiUrl(url), parsedResponse.userExceptionType ? ErrorTypes.USER_ERROR : ErrorTypes.REMOTE_ERROR, parsedResponse.payload, parsedResponse.message, parsedResponse.userExceptionType);
    }
}

/**
 * Perform a HTTP GET without a body and returns a local url.
 * @param {string} url - The url to get.
 * @param {Object.<string, *>?} queryParameters - Key/value map of query parameters to append to the url.
 * @param {string?} token - A JWT if the user is logged in.
 * @param {string} contentType - We can ask for json or images
 * @param {string} acceptType - We can ask for json or images
 * @param {bool} asUrl - if a url should be returned, othwerwise the blob is returned
 * @return {{payload: Object?, message: string, extraInfo: *}}
 * @throws {NetworkError}
 */
export async function getBlob(url, queryParameters, token, contentType = ContentTypes.JSON, acceptType = ContentTypes.JSON, asUrl = true) {
    const headers = new Headers();
    headers.append("Content-Type", contentType);
    headers.append("Accept", acceptType);
    if (token) {
        headers.append("Authorization", `Bearer ${token}`);
    }

    const mode = "cors";

    let httpResponse;
    try {
        const queryParameterString = (queryParameters && Object.values(queryParameters).find(p => p !== undefined) !== undefined) ?`?${createQueryParameters(queryParameters, [])}`:"";
        httpResponse = await fetch(`${makeApiUrl(url)}${queryParameterString}`, {method: "GET", headers, mode});
    } catch (error) {
        throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, error, null);
    }

    if (!httpResponse.ok) {
        let parsedError;
        try {
            parsedError = toCamelCase(await httpResponse.json());
        } catch (error) {
            if(process.env.NODE_ENV !== "production") {
                parsedError = {message:`${httpResponse.status.toString()} ${httpResponse.statusText}`};
            } else {
                parsedError = undefined;
            }
        }

        if (httpResponse.status === 401) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.UNAUTHORIZED, undefined, parsedError && parsedError.displayMessage, parsedError && parsedError.errorCodeAsText);
        } else if (httpResponse.status === 403) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.ACCESS_DENIED, undefined, parsedError && parsedError.displayMessage, parsedError && parsedError.errorCodeAsText);
        } else if (httpResponse.status >= 400 && httpResponse.status < 500) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.USER_ERROR, parsedError && parsedError.payload, parsedError && parsedError.displayMessage, parsedError && parsedError.errorCodeAsText);
        } else if ([502, 504].some(code => code === httpResponse.status)){
            throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, undefined, "Gateway Timeout");
        } else {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.REMOTE_ERROR, parsedError && parsedError.payload, parsedError && parsedError.displayMessage, parsedError && parsedError.errorCodeAsText);
        }
    }

    try {
        const myBlob = await httpResponse.blob();
        if( asUrl ){
            return URL.createObjectURL(myBlob, {autoRevoke : false});
        }
        return myBlob;
    } catch (error) {
        throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, error, null);
    }
}

export const methods = {
    POST: "POST",
    GET: "GET"
};

const doCall = async (dispatch, state, token, method, path, data, contentType = ContentTypes.JSON, acceptType = ContentTypes.JSON) => {
    try {
        let response;
        if (method === methods.POST) {
            response = await postJson(path, data, token, contentType, acceptType);
        } else {
            if( acceptType === ContentTypes.IMAGE) {
                response = await getBlob(path, data, token, contentType, acceptType);
            } else {
                response = await getJson(path, data, token, contentType, acceptType);
            }
        }
        return response;
    } catch (e) {
        if (e instanceof NetworkError && e.errorType === ErrorTypes.UNAUTHORIZED) {
            if (isLoggedIn(state.authentication)) {
                dispatch(logOut());
            }
        }
        
        throw e;
    }
};

/**
 * Thunk which asynchronously extracts state data necessary for contacting the API and calls a GET API function in standard form on SYS.
 * @param {string} path
 * @param {Object?} queryParameters 
 * @returns {{payload: Object?, message: string?}}
 * @throws {NetworkError}
 * 
 */
export function getSysApi(path, queryParameters) {
    return async (dispatch, getState) => {
        const state = getState();
        const token = state.authentication.token;
        return await doCall(dispatch, state, token, methods.GET, path, {
            ...queryParameters
        });
    };
}

/**
 * Thunk which asynchronously extracts state data necessary for contacting the API and calls a POST API function in standard form on SYS.
 * @param {string} path
 * @param {Object?} data 
 * @returns {{payload: Object?, message: string?}}
 * @throws {NetworkError}
 * 
 */
export function postSysApi(path, data) {
    return async (dispatch, getState) => {
        const state = getState();
        const token = state.authentication.token;
        return await doCall(dispatch, state, token, methods.POST, path, {
            ...data
        });
    };
}

/**
 * 
 * @param {*} path 
 * @param {*} queryParameters 
 * @param {*} contentType 
 * @param {*} acceptType 
 * @returns {*}
 */
export function getApi(path, queryParameters, contentType ,acceptType) {
    return async (dispatch, getState) => {
        const state = getState();
        const token = state.authentication.token;
        return await doCall(dispatch, state, token, methods.GET, path, {
            ...queryParameters
        }, contentType, acceptType);
    };
}

/**
 * 
 * @param {*} path 
 * @param {*} data 
 * @param {*} contentType 
 * @param {*} acceptType 
 * @returns {*}
 */
export function postApi(path, data, contentType, acceptType) {
    return async (dispatch, getState) => {
        const state = getState();
        const token = state.authentication.token;
        return await doCall(dispatch, state, token, methods.POST, `${path}`, {
            ...data
        }, contentType, acceptType);
    };
}


/**
 * Downloads a blob file to disk
 * @param {object} blob
 * @param {string} saveName name of the saved file
 * @param {string} contentType
 * @returns {*}
 */
export function downloadBlob(blob, saveName, contentType){
    // It is necessary to create a new blob object with mime-type explicitly set
    // otherwise only Chrome works like it should
    var newBlob = new Blob([blob], {type: contentType});
   
    // IE doesn't allow using a blob object directly as link href
    // instead it is necessary to use msSaveOrOpenBlob
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(newBlob, saveName);
        return;
    } 
   
    // For other browsers: 
    // Create a link pointing to the ObjectURL containing the blob.
    const data = window.URL.createObjectURL(newBlob);
    var link = document.createElement("a");
    link.href = data;
    link.download=saveName;
    link.click();
    setTimeout(function(){
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(data);
    }, 100);
}